import _ from "lodash";

import {
	SSO_ENDPOINT,
	PROVISION_ENDPOINT,
	MODEL_ENDPOINT,
	KEYCHAIN_ENDPOINT,
	REMOTE_ENDPOINT,
	EAPI_ENDPOINT,
	EAPI2_ENDPOINT,
	AUTH_ENDPOINT,
} from "./environment";

import $http from "@/utils/http";
import { getAuthToken } from "@/utils/cookie";

import { encodeQuery } from "../utils/query";

export default {
	descriptions: null,

	get $log() {
		return window.vm.$log;
	},

	async ssoLogin(email, password) {
		this.$log(`Cloud: Login ${email}`);

		let user = await $http.post(`${SSO_ENDPOINT}/auth/login`, {
			body: { email, password },
		});

		localStorage.setItem("k4ops_user_guid", user.guid);
		return user;
	},

	async jwtLogin(email, password) {
		const auth = "Basic " + btoa(`${email}:${password}`);
		const token = await $http.post(`${AUTH_ENDPOINT}`, {
			headers: {
				Authorization: auth,
			},
		});

		const parsedToken = JSON.parse(atob(token.split(".")[1]));
		let { cookie } = parsedToken;
		cookie = cookie.replace("k4connect.com", ".k4connect.com");

		// For local dev, if you need a localhost cookie
		// cookie = cookie.replace('k4connect.com', '');

		// Set domain cookie
		const cookieArr = cookie.split(";");
		cookieArr[0] = `jwt=${token}`;
		document.cookie = cookieArr.slice(0, 4).join(";");
	},

	async getRoles() {
		this.$log("Cloud: Fetching all user roles");
		const roles = $http.get(`${SSO_ENDPOINT}/roles`);
		return roles;
	},

	async getRolePermissions(roleId) {
		this.$log(`Cloud: Getting permission for role ID ${roleId}`);
		const resp = await $http.get(`${SSO_ENDPOINT}/roles/${roleId}/permission`);
		return resp.permissions;
	},

	async getCurrentUser() {
		let guid = localStorage.getItem("k4ops_user_guid");
		if (!guid) {
			throw new Error("User not logged in");
		}
		const userProps = await this.getUser(guid);
		return userProps;
	},

	async getUser(userId) {
		let user = await $http.get(
			`${SSO_ENDPOINT}/users/${userId}?properties=True`
		);

		if (_.has(user, "properties.password")) {
			delete user.properties.password;
		}

		return user;
	},

	async createUser(payload) {
		this.$log(`Cloud: Creating new user - ${JSON.stringify(payload)}`);
		const url = `${SSO_ENDPOINT}/users`;
		const resp = await $http.post(url, { body: payload });
		return resp;
	},

	async addUserProperty(guid, name, value) {
		this.$log(
			`Cloud: Adding property to user ${guid}: ${name}: ${JSON.stringify(
				value
			)}`
		);
		const url = `${SSO_ENDPOINT}/users/${guid}/properties`;
		const resp = await $http.post(url, { body: [{ name, value }] });
		return resp;
	},

	async addUserProperties(guid, nameValueArray) {
		this.$log(
			`Cloud: Adding properties to user ${guid}: ${nameValueArray
				.map(prop => `${prop.name}: ${JSON.stringify(prop.value)}`)
				.join("; ")}.`
		);
		const url = `${SSO_ENDPOINT}/users/${guid}/properties`;
		const resp = await $http.post(url, { body: nameValueArray });
		return resp;
	},

	async removeUserProperty(guid, name) {
		const url = `${SSO_ENDPOINT}/users/${guid}/properties/${name}`;
		return $http.delete(url);
	},

	async addUserRole(guid, roleId) {
		this.$log(`Cloud: Adding role ${roleId} to user ${guid}`);
		const url = `${SSO_ENDPOINT}/roles/${roleId}/grant`;
		const resp = await $http.post(url, { body: { user: guid } });
		return resp;
	},

	async revokeUserRole(guid, roleId) {
		this.$log(`Cloud: Revoking role ${roleId} from user ${guid}`);
		const url = `${SSO_ENDPOINT}/roles/${roleId}/revoke`;
		const resp = await $http.post(url, { body: { user: guid } });
		return resp;
	},

	async logout() {
		this.$log(`Cloud: logout`);

		await $http.post(`${SSO_ENDPOINT}/auth/logout`);

		document.cookie = "jwt=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=.k4connect.com";

		return true;
	},

	async getSystem(systemId) {
		this.$log(`Cloud: Get info for system ${systemId}`);

		const resp = await $http.get(
			`${PROVISION_ENDPOINT}/system/${systemId}`
		);
		return resp;
	},

	async getSystems(groupId) {
		this.$log(`Cloud: Get systems for groupId ${groupId}`);

		let systems = await $http.get(
			`${PROVISION_ENDPOINT}/systems?groupId=${groupId}`
		);

		_.each(systems, system => {
			// if we have an install report, we mark it as installed
			system.installed = typeof system.data === "string";
		});

		return systems;
	},

	async getBySerial(serial) {
		this.$log(`Cloud: Get group and system by ${serial}`);

		let community, system;

		let mac = serial.match(/.{1,2}/g).join(":");
		let [found] = await $http.get(
			`${PROVISION_ENDPOINT}/system/controllers?mac=${mac}`
		);

		if (!found) {
			throw new Error("serial not found");
		}

		let communities = await this.getCommunities();

		community = _.find(communities, item => {
			return item.identifier === String(found.groupId);
		});

		let systems = await this.getSystems(found.groupId);

		system = _.find(systems, item => {
			return item.id === found.sysid;
		});

		return {
			system,
			community,
		};
	},

	async getDescriptions() {
		this.$log(`Cloud: Get descriptions`);

		// descriptions doesnt change often, so only get once
		if (!this.descriptions) {
			this.descriptions = await $http.get(`${PROVISION_ENDPOINT}/description`);
		}

		return this.descriptions;
	},

	async getCredentials(systemId) {
		this.$log(`Cloud: Get credentials for ${systemId}`);

		let credentials = await $http.get(
			`${KEYCHAIN_ENDPOINT}/checkoutSystem/${systemId}`
		);
		return credentials;
	},

	async getSystemAdmin(systemId) {
		this.$log(`Cloud: Get system admin for ${systemId}`);

		let credentials = await $http.get(`${SSO_ENDPOINT}/sysadmin/${systemId}`);
		return credentials;
	},

	async getSystemUsers(systemId) {
		this.$log(`Cloud: Get users for ${systemId}`);

		let users = await $http.get(`${SSO_ENDPOINT}/system/${systemId}/users`);
		return users;
	},

	async getSystemGroup(systemId) {
		this.$log(`Cloud: Get community ID for system ${systemId}`);

		const resp = await $http.get(
			`${PROVISION_ENDPOINT}/system/${systemId}/group`
		);
		return resp;
	},

	async getControllerSerial(systemId) {
		this.$log(`Cloud: Get controller serial for ${systemId}`);

		let controller = await this.getController(systemId);

		let mac = _.get(controller, "properties.mac");
		if (mac) {
			return mac.replace(/:/g, "");
		}
	},

	async getLastKnown(systemId) {
		this.$log(`Cloud: Get last known for ${systemId}`);

		let controller = await this.getController(systemId);

		return _.get(controller, "variables.wifi.ipaddress");
	},

	async getController(systemId) {
		this.$log(`Cloud: Get controller for ${systemId}`);

		let controller;

		let devices = await $http.get(`${MODEL_ENDPOINT}/${systemId}/Devices`);

		controller = this.findNode(devices, "com.k4connect.controller");

		return controller;
	},

	async getDevice(systemId, devicePath) {
		this.$log(`Cloud: Get device for ${systemId} - ${devicePath}`);

		return $http.get(`${MODEL_ENDPOINT}/${systemId}/${devicePath}`);
	},

	async getControllerRemote(systemId, ctoken) {
		this.$log(`Cloud: Get controller remote address for ${systemId}`);
		const retval = await $http.get(
			`${REMOTE_ENDPOINT}/remote?sysid=${systemId}&ctoken=${ctoken}`
		);
		return retval;
	},

	async submitReport(systemId, report) {
		this.$log(`Cloud: Submit report for ${systemId}`);

		report = typeof report !== "string" ? JSON.stringify(report) : report;

		let response = await $http.post(
			`${PROVISION_ENDPOINT}/system/${systemId}/install`,
			{
				body: {},
			}
		);

		this.$log(`Cloud: Marking ${systemId} as installed: ${response}`);

		response = await $http.post(
			`${PROVISION_ENDPOINT}/system/${systemId}/installReport`,
			{
				body: {
					installationData: report,
				},
			}
		);

		this.$log(`Cloud: Submitting report for ${systemId}: ${response}`);
	},

	async getAssociations({ type } = {}) {
		let url = `${SSO_ENDPOINT}/associations`;
		let message = "Cloud: Retrieving associations";
		if (type) {
			url += `?type=${type}`;
			message += ` of type: ${type}`;
		}
		this.$log(message);

		const resp = await $http.get(url);
		return resp;
	},

	async findAssociationByIdentifier(identifier) {
		let url = `${SSO_ENDPOINT}/associations/findByIdentifier?identifier=${identifier}`;
		this.$log("Cloud: Retrieving association data from " + identifier);
		return $http.get(url);
	},

	async getCommunities() {
		let communities = await this.getAssociations({ type: "community" });
		return communities;
	},

	async addAssociationUser(userId, associationId) {
		this.$log(`Cloud: Adding user ${userId} to association ${associationId}`);

		const url = `${SSO_ENDPOINT}/associations/${associationId}/users/add`;
		const resp = await $http.post(url, { body: { user: userId } });
		return resp;
	},

	async removeAssociationUser(userId, associationId) {
		this.$log(
			`Cloud: Removing user ${userId} from association ${associationId}`
		);
		const url = `${SSO_ENDPOINT}/associations/${associationId}/users/remove`;
		const resp = $http.post(url, { body: { user: userId } });
		return resp;
	},

	async getAssociationUsers(assocId, params) {
		params = _.defaults(params, {
			type: "all",
			status: "active",
			properties: true,
		});

		this.$log(
			`Cloud: Getting users with association ${assocId}: ${JSON.stringify(
				params
			)}`
		);

		let query = [];
		_.forOwn(params, (value, key) => {
			if (!_.isNil(value)) query.push(`${key}=${value}`);
		});
		query = query.join("&");

		const url = `${SSO_ENDPOINT}/associations/${assocId}/users?${query}`;
		const resp = await $http.get(url);
		return _.get(resp, "users", resp);
	},

	async updateAssociation(associationId, params) {
		this.$log(
			`Cloud: Updating association ${associationId}: ${JSON.stringify(params)}`
		);
		let url = `${SSO_ENDPOINT}/associations/${associationId}`;

		try {
			return $http.post(url, { method: "put", body: params });
		} catch (e) {
			if (e.name === "APIAuthError")
				return "error";
			throw e;
		}
	},

	async addAssociation(params) {
		this.$log(
			`Cloud: Adding association ${params.identifier}: ${JSON.stringify(params)}`
		);
		let url = `${SSO_ENDPOINT}/associations`;

		return $http.post(url, { body: params });
	},

	async getAccessGroups() {
		const accessGroups = await this.getAssociations({
			type: "association_group",
		});
		return accessGroups;
	},

	async getAccessGroupCommunities(groupId) {
		this.$log(
			`Cloud: Getting communities associated with access group ${groupId}`
		);

		const url = `${SSO_ENDPOINT}/associations/groups/${groupId}/associations`;
		const resp = await $http.get(url);
		return resp;
	},

	async createAccessGroup(name) {
		this.$log(`Cloud: Creating access group with name: ${name}`);

		const url = `${SSO_ENDPOINT}/associations/groups`;
		const resp = await $http.post(url, { body: { name } });
		return resp;
	},

	async addAccessGroupAssociation(groupId, associationId) {
		this.$log(
			`Cloud: Adding community ${associationId} to access group ${groupId}`
		);

		const url = `${SSO_ENDPOINT}/associations/groups/${groupId}/associations/add`;
		const resp = await $http.post(url, { body: { associationId } });
		return resp;
	},

	async removeAccessGroupAssociation(groupId, associationId) {
		this.$log(
			`Cloud: Removing community ${associationId} from access group ${groupId}`
		);

		const url = `${SSO_ENDPOINT}/associations/groups/${groupId}/associations/remove`;
		const resp = await $http.post(url, { body: { associationId } });
		return resp;
	},

	findNode(node, cls) {
		if (node.meta && node.meta.cls === cls) {
			return node;
		} else {
			let found;
			_.each(node, child => {
				if (child.meta) {
					found = this.findNode(child, cls);
				}
				if (found) {
					return false;
				} // break
			});

			return found;
		}
	},

	/**
	 * This code is all temporary for the existing provisioning server. It should be replaced soon by Provisioning 2.0 (K4Model Merge) using K4Model to make changes.
	 * Keeping it isolated here to make later replacement easier.
	 */

	// maps of nodes for quick lookup
	paths: {},
	devices: {},
	rootNode: null,

	/**
	 * These are the API functions that ultimately will be rewired to K4Model
	 */
	async getDevices(systemId) {
		this.$log(`Cloud: Get devices for ${systemId}`);

		await this.refreshDeviceTree(systemId);
		return this.devices;
	},

	async getRootNode(systemId) {
		if (!this.rootNode) {
			await this.refreshDeviceTree(systemId);
		}
		return this.rootNode;
	},

	async addDevice(name, parent, subpath, cls, system) {
		this.$log(
			`Cloud: Adding device ${name} with class ${cls} to ${parent} and subpath ${subpath} on system ${system}`
		);

		await $http.post(`${PROVISION_ENDPOINT}/deviceOverride`, {
			body: {
				system: system,
				type: "add",
				name: name,
				subpath: subpath,
				cls: cls,
				parent: parent,
			},
		});

		await this.refreshDeviceTree(system);

		return this.devices;
	},

	async renameDevice(device, name, system) {
		this.$log(
			`Cloud: Rename device ${device.name} to ${name} on system ${system}`
		);

		await $http.post(`${PROVISION_ENDPOINT}/device/${device.guid}/rename`, {
			body: {
				name: name,
			},
		});

		await this.refreshDeviceTree(system);

		return this.devices;
	},

	async moveDevice(device, destination, system) {
		this.$log(
			`Cloud: Move device ${device.name} to ${destination} on system ${system}`
		);

		await $http.post(`${PROVISION_ENDPOINT}/device/${device.guid}/move`, {
			body: {
				destination: destination,
			},
		});

		await this.refreshDeviceTree(system);

		return this.devices;
	},

	async removeDevice(device, system) {
		this.$log(`Cloud: Remove device ${device.path} on system ${system}`);

		let existing;
		try {
			existing = await $http.get(
				`${PROVISION_ENDPOINT}/deviceOverride/${device.guid}`
			);
		} catch (e) {
			if (e.response.status !== 404) {
				throw e;
			}
		}

		if (existing) {
			// device was an override, delete it
			await $http.delete(`${PROVISION_ENDPOINT}/deviceOverride/${device.guid}`);
		} else {
			// device was from template, add override
			await $http.post(`${PROVISION_ENDPOINT}/deviceOverride`, {
				body: {
					system,
					device: device.guid,
					type: "remove",
					parent: device.parent,
					subpath: device.subpath || null,
				},
			});
		}

		return device;
	},

	async setProperty(device, name, value, valueType) {
		await $http.post(`${PROVISION_ENDPOINT}/propertyOverride`, {
			body: {
				device,
				name,
				value,
				valueType,
			},
		});
	},

	async detachA4HDevice(dsn) {
		this.$log(`Cloud: Removing all associations for A4H device ${dsn}`);

		const resp = await $http.delete(
			`${EAPI_ENDPOINT}/devices/alexa/pair?dsn=${dsn}`
		);

		return resp;
	},

	async attachA4HDevice(dsn, communityName, roomName) {
		this.$log(
			`Cloud: Attaching A4H device ${dsn} to community ${communityName} and room ${roomName}`
		);

		const resp = await $http.post(`${EAPI_ENDPOINT}/devices/alexa/pair`, {
			body: {
				dsn,
				communityName,
				roomName,
			},
		});

		return resp;
	},

	/**
	 * These are the helper functions that currently use the old provisioning API
	 */

	async refreshDeviceTree(systemId) {
		this.paths = {};
		this.devices = {};

		// make sure we have descriptions
		await this.getDescriptions();

		let tree = await $http.get(
			`${PROVISION_ENDPOINT}/systemDeviceTree?system=${systemId}`
		);

		this.walkDevices(tree);
	},

	// recursively walk tree of devices and build our lists
	walkDevices(nodes, base) {
		_.each(nodes, (node, guid) => {
			let path;

			if (node.name !== "model") {
				// store the nodes to maps
				path = base ? `${base}/${node.name}` : node.name;
				this.buildNode(node, path, guid);
			} else {
				node.guid = guid;
				this.rootNode = node;
			}

			// walk the children
			this.walkDevices(node.children, path);

			// walk the subpaths
			this.walkSubpaths(node.subpaths, path);
		});
	},

	// a node can have a map of subpaths. Walk them, then pass each back to walkDevices
	walkSubpaths(subpaths, base) {
		_.each(subpaths, (subpath, i) => {
			let path = base ? `${base}/${i}` : i;
			this.walkDevices(subpath, path);
		});
	},

	// store helper maps so we can find nodes quickly later
	buildNode(node, path, guid) {
		// set some missing properties
		node.path = path;
		node.guid = guid;

		// build maps
		this.paths[path] = node;
		this.devices[node.guid] = node;

		// associate the description
		node.description = this.descriptions[node.cls];

		// build the properties list

		this.buildProperties(node);

		// set configured

		this.getConfigured(node);
	},

	// combine propertyOverrides and virtualProperties
	buildProperties(node) {
		node.properties = {};

		_.each(node.virtualProperties, property => {
			node.properties[property.name] = property;
		});

		// overrides always comes second
		_.each(node.propertyOverrides, property => {
			node.properties[property.name] = property;
		});
	},

	// determine if a device is configured or not
	getConfigured(node) {
		let configured = true;

		_.each(node.description.meta.properties, (property, i) => {
			let value;

			// check if there is a value
			let existing = _.find(node.properties, prop => {
				return prop.name === i;
			});

			if (existing) {
				existing.required = property.required;
				existing.configurable = property.configurable;
				value = existing.value;
			}

			// if its requried and there is no value

			if (property.required && (value === undefined || value === null)) {
				configured = false;
			}
		});

		_.each(node.properties, property => {
			let meta = node.description.meta.properties[property.name];
			if (meta) {
				property.required = meta.required;
				property.configurable = meta.configurable;
			}

			if (property.required && property.value === null) {
				configured = false;
			}
		});

		node.configured = configured;
	},

	// K4NOC ENDPOINTS

	// retrieve list of room move tickets
	async getRoomMoveTickets() {
		this.$log("Cloud: Get room move tickets.");

		return $http.get(`${SSO_ENDPOINT}/salesforce?search=New Resident&search=Move&search=Move Out`);
	},

	// retrieve list of room move tickets
	async getRoomMoveTicket(id) {
		this.$log(`Cloud: Get room move ticket for id ${id}.`);

		const { users } = await $http.get(`${SSO_ENDPOINT}/users/properties/UserCreationTicket/${id}`) || { users: [] };

		return users
			.map(
				({ password, passwordSalt, passwordVersion, ...user }) =>
				({
					...user,
					properties: user.properties
						.reduce((props, item) => {
							props[item.name] = item.value;
							return props;
						}, {}),
				})
			);
	},

	async getControllerAlerts(params = {}) {
		this.$log(`Cloud: Getting controller alerts with params ${JSON.stringify(params)}.`);
		const query = encodeQuery(params);

		let url = `${EAPI2_ENDPOINT}/devices/alarms`;

		if (query) url += "?" + query;
		const res = await $http.get(url, {
			headers: {
				Authorization: `Bearer ${getAuthToken()}`,
			},
		});

		return res;
	},

	async getActiveAlerts(sysId) {
		this.$log(`Cloud: Getting room active alerts for system ${sysId}.`);

		const url = `${EAPI2_ENDPOINT}/devices/alarms/system/${sysId}?alarmStatus=ACTIVE&alarmStatus=ACKNOWLEDGED`;

		return $http.get(url, {
			headers: {
				Authorization: `Bearer ${getAuthToken()}`,
			},
		});
	},

	async getAlertHistory(sysId, params = {}) {
		this.$log(`Cloud: Getting room alert history for system ${sysId}.`);
		const query = encodeQuery(params);

		const url = `${EAPI2_ENDPOINT}/devices/alarms/history/system/${sysId}?${query}`;

		return $http.get(url, {
			headers: {
				Authorization: `Bearer ${getAuthToken()}`,
			},
		});
	},

	async changeAlertStatus({ id, ...body }) {
		const res = await $http.post(`${EAPI2_ENDPOINT}/devices/alarms/${id}`, {
			method: "put",
			headers: {
				Authorization: `Bearer ${getAuthToken()}`,
			},
			body,
		});
		return res;
	},

	async reenableAlert({ id, ...body }) {
		const res = await $http.post(
			`${EAPI2_ENDPOINT}/devices/alarms/enable/${id}`,
			{
				method: "put",
				headers: {
					Authorization: `Bearer ${getAuthToken()}`,
				},
				body,
			}
		);
		return res;
	},


	// LOCK MANAGER ENDPOINTS
	async locksLogin() {
		this.$log(`Locks: Login operation.`);

		return $http.get(`${PROVISION_ENDPOINT}/locks/login`);
	},

	async getLocks(communityId) {
		this.$log(`Locks: Fetching locks for community ${communityId}.`);

		return $http.get(`${PROVISION_ENDPOINT}/locks?communityId=${communityId}`);
	},

	async addLock(communityId, deviceId, roomName, creds) {
		this.$log(`Locks: Adding lock ${deviceId} to community ${communityId}.`);

		return $http.post(`${PROVISION_ENDPOINT}/locks`, { body: { communityId, deviceId, roomName, creds } });
	},

	async removeLock(communityId, deviceId) {
		this.$log(`Locks: Removing lock ${deviceId} from community ${communityId}`);

		return $http.post(`${PROVISION_ENDPOINT}/locks`, {
			method: "delete", body: { communityId, deviceId }
		});
	},

	async checkoutLock(deviceId) {
		this.$log(`Locks: Checkout lock ${deviceId}.`);

		return $http.post(`${PROVISION_ENDPOINT}/locks/checkout`, { body: { deviceId } });
	},

	async verifyLock(allyToken) {
		this.$log(`Locks: Verifying lock credentials.`);

		return $http.post(`${PROVISION_ENDPOINT}/locks/verify`, { body: { allyToken } });
	},

	async getLockUsers(communityId) {
		this.$log(`Locks: Fetching users for community ${communityId}.`);

		return $http.get(`${PROVISION_ENDPOINT}/locks/users?communityId=${communityId}`);
	},

	// About adding/removing permissions, we could use the
	// current methods of addUserRole and revokeUserRole
	// NOTE backend returning 500
	async addLockUser({ userId, admin } = {}) {
		this.$log(`Locks: Adding user ${userId} with ${admin ? 'admin' : 'user'} access.`);

		return $http.post(`${PROVISION_ENDPOINT}/locks/user`, { body: { userId, admin } });
	},

	// NOTE backend returning 500
	async removeLockUser(userId) {
		this.$log(`Locks: Removing access from user ${userId}.`);

		return $http.delete(`${PROVISION_ENDPOINT}/locks/user/${userId}`);
	},
};
